/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { ProductSearchResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { searchByCategoryAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

import getCurrentCategory, { CurrentCategoryInput } from './get-current-category';
import getProducts, { ProductInput } from './get-simple-products';
import { QueryResultSettingsProxy } from './utilities/QueryResultSettingsProxy';
import { generateCacheKey } from './utilities/utils';

/**
 * Product by category ID Input action.
 * @deprecated
 */
export class ProductsByCategoryInput implements IActionInput {
    public categoryId?: number;

    public categorySlug?: string;

    public categoryName?: string;

    public catalogId: number;

    public currentCategory: CurrentCategoryInput;

    public readonly queryResultSettingsProxy: QueryResultSettingsProxy;

    private readonly apiSettings: ICommerceApiSettings;

    constructor(category: CurrentCategoryInput, apiSettings: ICommerceApiSettings, queryResultSettingsProxy: QueryResultSettingsProxy) {
        this.apiSettings = apiSettings;
        this.currentCategory = category;
        this.queryResultSettingsProxy = queryResultSettingsProxy;
        this.catalogId = apiSettings.catalogId;
        this.categoryId = category.categoryId;
        this.categorySlug = category.categorySlug;
    }

    public getCacheKey = () =>
        generateCacheKey(
            this.apiSettings,
            { key: 'CategoryId', value: this.categoryId || this.categorySlug },
            { key: 'CatalogId', value: this.catalogId },
            { key: 'QueryResultSettingsCacheKeyHint', value: this.queryResultSettingsProxy.cacheKeyHint }
        );

    public getCacheObjectType = () => 'Products-From-Search';

    public dataCacheType = (): CacheType => 'application';
}

/**
 * CreateInput method for the getProductsByCategory data aciton.
 * @param inputData
 * @deprecated
 */
export const createGetProductsByCategoryInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    if (inputData && inputData.requestContext) {
        const currentCategory = new CurrentCategoryInput(inputData.requestContext);
        const queryResultSettingsProxy = QueryResultSettingsProxy.fromInputData(inputData);
        return new ProductsByCategoryInput(currentCategory, inputData.requestContext.apiSettings, queryResultSettingsProxy);
    }

    throw new Error('Please specify categoryId query string in request.');
};

/**
 * Action method for the getProductsByCategory data action.
 * @param input
 * @param ctx
 * @deprecated
 */
export async function getProductsByCategoryAction(input: ProductsByCategoryInput, ctx: IActionContext): Promise<SimpleProduct[]> {
    const { apiSettings } = ctx.requestContext;
    let categoryId = input.currentCategory.categoryId;
    if (input.currentCategory.categorySlug && !categoryId) {
        const category = await getCurrentCategory(input.currentCategory, ctx);
        if (!category) {
            ctx.trace('[getProductsByCategoryAction] Unable to find category');
            return <SimpleProduct[]>[];
        }
        categoryId = category.RecordId;
    }

    const productInputs = await searchByCategoryAsync(
        { callerContext: ctx, queryResultSettings: input.queryResultSettingsProxy.QueryResultSettings },
        +apiSettings.channelId,
        input.catalogId,
        categoryId || 0
    ).then(productSearchResults => {
        return productSearchResults.map(
            (product: ProductSearchResult): ProductInput => {
                return new ProductInput(product.RecordId, apiSettings, undefined, undefined, ctx.requestContext);
            }
        );
    });

    if (productInputs.length > 0) {
        // eslint-disable-next-line no-return-await -- existing code
        return await getProducts(productInputs, ctx);
    }
    return <SimpleProduct[]>[];
}

/**
 * The getProductsByCategory data action
 * Retrieves the current category of the request via the getCurrentCategory data action
 * Then calls the searchByCategory RetailServer API to get a list of products associated with
 * the current category, and finally fully hydrates the data for those prodcuts via the
 * getProducts data action, returning a list of SimpleProducts within the current category.
 * @deprecated
 */
export const getProductsByCategoryActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-products-by-category',
    action: <IAction<SimpleProduct[]>>getProductsByCategoryAction,
    input: createGetProductsByCategoryInput
});

/**
 * @deprecated
 */
export default getProductsByCategoryActionDataAction;
